'use client';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { Radio } from '@pickleballinc/react-ui';
import { useRouter, useSearchParams } from 'next/navigation';
import { Fragment, useCallback, useEffect, useState, type TouchEvent } from 'react';

import { WORLD_PICKLEBALL_RANKINGS } from '@/modules/rankings/constants';
import { BEST_TOURNAMENTS_COUNT } from '@/modules/rankings/ppr/constants';
import {
  getDivisionLabel,
  getDivisionTournamentsPlayed,
  getProWorldRankingShare,
  parsePprDivisionType,
} from '@/modules/rankings/ppr/helpers';
import { RankingTooltip } from '@/modules/rankings/ppr/player-profile/RankingTooltip';
import { EventMatchCard } from '@/modules/rankings/ppr/ranking-history/EventMatchCard';
import { RankingPlayerProfileData, TournamentStats } from '@/modules/rankings/ppr/types';

export function PickleballRankingHistory({
  rankingPlayerDetails,
  playerData,
  hasOtherTournaments,
}: {
  rankingPlayerDetails: TournamentStats;
  playerData: RankingPlayerProfileData;
  hasOtherTournaments: boolean;
}) {
  const filters = [
    { id: 'best', filter: 'best', title: `Best ${BEST_TOURNAMENTS_COUNT} Tournaments` },
    ...(hasOtherTournaments ? [{ id: 'other', filter: 'other', title: 'Other Tournaments' }] : []),
  ];
  const searchParams = useSearchParams();
  const router = useRouter();
  const selectedFilter = searchParams?.get('filter') === 'other' ? 'other' : 'best';
  const divisionType = parsePprDivisionType(searchParams?.get('division_type'));
  const { multiplier: pwrMultiplier, label: pwrLabel } = getProWorldRankingShare(divisionType);
  const divisionLabel = getDivisionLabel(divisionType);
  const firstTournamentUuid = rankingPlayerDetails.tournamentSummaries[0]?.tournamentUuid;
  const [expandedBrackets, setExpandedBrackets] = useState<{
    [uuid: string]: boolean;
  }>(firstTournamentUuid ? { [firstTournamentUuid]: true } : {});
  const [isWeeks52TooltipOpen, setIsWeeks52TooltipOpen] = useState(false);

  const handleWeeks52TouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsWeeks52TooltipOpen(true);
  }, []);

  useEffect(() => {
    if (!isWeeks52TooltipOpen) return;
    const close = () => setIsWeeks52TooltipOpen(false);
    document.addEventListener('touchstart', close);
    return () => document.removeEventListener('touchstart', close);
  }, [isWeeks52TooltipOpen]);

  const handleChange = (value?: string) => {
    if (!value || value === selectedFilter) return;

    const newSearchParams = new URLSearchParams(searchParams?.toString() || '');

    if (value === 'other') {
      newSearchParams.set('filter', 'other');
    } else {
      newSearchParams.delete('filter');
    }

    newSearchParams.set('page', '1');
    router.replace(`?${newSearchParams.toString()}`, { scroll: false });
  };

  const toggleBrackets = (uuid: string) => {
    setExpandedBrackets((prev) => ({
      ...prev,
      [uuid]: !prev[uuid],
    }));
  };

  return (
    <div className='bg-white font-sans text-gray-900'>
      <div className='max-w-7xl mx-auto px-4 xl:px-0'>
        {/* Filters */}
        <div className='flex flex-col md:flex-row md:items-center justify-between gap-4'>
          <div className='flex flex-col'>
            <div className='flex items-center gap-2 pb-2'>
              <span className='text-lg font-semibold text-gray-800'>{`${formatDateTime(rankingPlayerDetails.startDate)} - ${formatDateTime(rankingPlayerDetails.endDate)}`}</span>
              <RankingTooltip
                open={isWeeks52TooltipOpen}
                onOpenChange={setIsWeeks52TooltipOpen}
                handleTouch={handleWeeks52TouchStart}
                content={"52 Weeks is the accumulation of a player's points over the past 52 weeks"}
              />
            </div>
            <span className='text-sm font-medium text-gray-700 sm:text-base pb-0 sm:pb-2'>
              {`Total Tournaments played in ${divisionLabel}: ${getDivisionTournamentsPlayed(divisionType, rankingPlayerDetails)}`}
            </span>
            <div className='flex flex-wrap items-center gap-4 text-[11px] font-medium text-gray-600'>
              <div className='h-[56px] rounded-lg hidden sm:flex justify-left items-start gap-x-2'>
                {filters.map((filter) => (
                  <Radio
                    key={`topn-${filter.id}`}
                    name='tournamentsFilter'
                    id={`topn-${filter.id}`}
                    value={filter.filter}
                    Text={filter.title}
                    checked={selectedFilter === filter.filter}
                    onChange={(e) => handleChange(e.currentTarget.value)}
                  />
                ))}
              </div>

              {/* MOBILE */}
              <div className='rounded-lg sm:hidden overflow-x-scroll flex gap-2 py-4'>
                {filters.map((filter) => (
                  <Radio
                    key={`topn-mobile-${filter.id}`}
                    name='tournamentsFilterMobile'
                    id={`topn-mobile-${filter.id}`}
                    value={filter.filter}
                    Text={filter.title}
                    checked={selectedFilter === filter.filter}
                    onChange={(e) => handleChange(e.currentTarget.value)}
                    size='sm'
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className='flex flex-col md:hidden md:text-right text-left md:text-lg text-base text-gray-900 mb-4'>
          <span>
            {`${WORLD_PICKLEBALL_RANKINGS} Points: ${Math.round(rankingPlayerDetails.totalPoints * pwrMultiplier)} pts (${pwrLabel})`}
          </span>
          <span>{`${divisionLabel} Points: ${rankingPlayerDetails.totalPoints} pts`}</span>
        </div>
        <div className='md:flex hidden text-right md:text-base text-base text-gray-900 justify-end mb-4'>
          {`${WORLD_PICKLEBALL_RANKINGS} Points: `}
          <span className='mx-1'>
            {`${Math.round(rankingPlayerDetails.totalPoints * pwrMultiplier)} pts (${pwrLabel})`}
          </span>
          {` | ${divisionLabel} Points: `}
          <span className='mx-1'>{`${rankingPlayerDetails.totalPoints} pts`}</span>
        </div>

        {/* Tournaments List */}
        <div className='rounded-lg border border-gray-300'>
          <div className='flex h-[56px] items-center px-3 text-gray-700 text-sm font-semibold bg-gray-50 rounded-t-lg justify-between'>
            <p>
              Tournaments
              <span className='ml-1'>{`(${rankingPlayerDetails?.tournamentSummaries.length})`}</span>
            </p>
            <p>Points Earned</p>
          </div>
          {rankingPlayerDetails.tournamentSummaries.map((item) => {
            const tournamentMaxPoints = item.cumulatedPoints;
            return (
              <Fragment key={item.tournamentUuid}>
                <div className='min-h-[72px] px-3 bg-[#351684] flex flex-col sm:flex-row sm:items-center justify-between text-white py-3 sm:py-2'>
                  <div className='flex flex-col mb-4 sm:mb-0'>
                    <p className='font-semibold sm:text-base text-sm flex flex-col sm:line-clamp-1'>
                      {item.tournamentTitle}
                    </p>
                    <p className='font-normal sm:text-base text-sm'>
                      {`${formatDateTime(item.tournamentStartDate)} - ${formatDateTime(item.tournamentEndDate)}`}
                    </p>
                  </div>
                  <div className='flex flex-col gap-y-3 sm:flex-row gap-x-3 items-stretch'>
                    <div className='flex items-center justify-center gap-x-2 rounded-md min-h-14 bg-[#5925DC] px-2 border border-white py-1'>
                      <div className='flex items-center justify-center flex-shrink-0' />
                      <div className='min-w-0 text-sm font-normal text-center flex-col'>
                        <div className='flex flex-col md:flex-row gap-1'>
                          <p className='text-sm text-center md:whitespace-nowrap'>
                            {`${WORLD_PICKLEBALL_RANKINGS} Points: ${Math.round(tournamentMaxPoints * pwrMultiplier)} pts (${pwrLabel})`}
                          </p>
                          <span className='hidden md:flex'>|</span>
                          <p className='text-sm text-center md:whitespace-nowrap'>
                            {`${divisionLabel} Points: ${tournamentMaxPoints} pts`}
                          </p>
                        </div>
                        <p className='text-sm text-center md:whitespace-nowrap'>{`Drops ${formatDateTime(item.tournamentEndDate)}`}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  className='min-h-[56px] flex items-center w-full justify-between px-4 py-2 cursor-pointer'
                  onClick={() => toggleBrackets(item.tournamentUuid)}
                >
                  <div className='text-sm font-semibold text-gray-700'>Matches</div>
                  <div className='flex items-center justify-center py-2 px-2.5'>
                    <FontAwesomeIcon
                      icon={expandedBrackets[item.tournamentUuid] ? faChevronUp : faChevronDown}
                      className='!text-brand-800'
                    />
                  </div>
                </button>
                {expandedBrackets[item.tournamentUuid]
                  ? item.events.map((event) => (
                      <div className='p-3' key={event.eventUuid}>
                        <EventMatchCard
                          event={event}
                          playerName={playerData.fullName}
                          isExpanded
                          tournamentUuid={item.tournamentUuid}
                        />
                      </div>
                    ))
                  : null}
              </Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
